import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

const REPLYABLE_BUTTON_TYPES = new Set(['QUICK_REPLY', 'FLOW']);
const getTemplateReplyButtons = (tpl) => {
    if (!tpl || !Array.isArray(tpl.buttons)) return [];
    return tpl.buttons
        .filter(b => {
            if (typeof b === 'string') return true;
            const btnType = String(b.type || '').toUpperCase();
            return !btnType || REPLYABLE_BUTTON_TYPES.has(btnType);
        })
        .map(b => (typeof b === 'string' ? b : b.text))
        .filter(Boolean);
};

// Simplified ButtonActionsEditor for Leads Modal
const ButtonActionsEditor = ({ buttons, actions, onChange, templates, followUpWorkflows, depth = 0 }) => {
    const currentActions = actions || {};

    const updateBtn = (btnName, field, value) => {
        const next = { ...currentActions };
        const cfg = next[btnName] || { action: 'CONTINUE', reply_text: '' };
        next[btnName] = { ...cfg, [field]: value };
        onChange(next);
    };

    const updateBtnVar = (btnName, varIdx, value) => {
        const next = { ...currentActions };
        const cfg = next[btnName] || { action: 'SEND_TEMPLATE', template_variables: { body_variables: [] } };
        const tplVars = { ...(cfg.template_variables || { body_variables: [] }) };
        const bodyVars = [...(tplVars.body_variables || [])];
        bodyVars[varIdx] = value;
        tplVars.body_variables = bodyVars;
        next[btnName] = { ...cfg, template_variables: tplVars };
        onChange(next);
    };

    const removeBtn = (btnName) => {
        const next = { ...currentActions };
        delete next[btnName];
        onChange(next);
    };

    const allBtnNames = Array.from(new Set([...(buttons || []), ...Object.keys(currentActions)]));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {allBtnNames.length > 0 && allBtnNames.map(btnName => {
                const actionCfg = currentActions[btnName] || { action: 'CONTINUE', reply_text: '' };
                const triggeredTpl = actionCfg.action === 'SEND_TEMPLATE'
                    ? templates.find(t => String(t.id) === String(actionCfg.template_id))
                    : null;

                let tplVarCount = 0;
                if (triggeredTpl) {
                    if (triggeredTpl.variables_count) tplVarCount = triggeredTpl.variables_count;
                    else if (triggeredTpl.body) {
                        const m = triggeredTpl.body.match(/\{\{([^}]+)\}\}/g);
                        if (m) tplVarCount = m.length;
                    }
                }

                const nestedButtons = triggeredTpl ? getTemplateReplyButtons(triggeredTpl) : [];
                const hasNestedConfig = Object.keys(actionCfg.next_button_actions || {}).length > 0;

                return (
                    <div key={btnName} style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--brand-purple)' }}>Button: "{btnName}"</span>
                            <button
                                type="button"
                                onClick={() => removeBtn(btnName)}
                                style={{ background: 'transparent', border: 'none', color: '#f87171', fontSize: '10px', cursor: 'pointer', fontWeight: '600' }}
                            >
                                Reset Action
                            </button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', alignItems: 'center' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>Action On Click</label>
                            <select
                                value={actionCfg.action || 'CONTINUE'}
                                onChange={e => updateBtn(btnName, 'action', e.target.value)}
                                style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '11px', outline: 'none', background: '#fff' }}
                            >
                                <option value="CONTINUE">🟢 Continue / No specific action</option>
                                <option value="STOP">🔴 Stop / Divert Reminders for Contact</option>
                                <option value="SEND_TEMPLATE">📩 Send Template Message</option>
                                <option value="TRIGGER_FOLLOWUP">🔁 Trigger Follow-up Workflow</option>
                                <option value="FOLLOWUP_REPLY">💬 Send Custom Text Reply</option>
                            </select>
                        </div>

                        {actionCfg.action === 'SEND_TEMPLATE' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px', background: '#f1f5f9', padding: '8px', borderRadius: '6px' }}>
                                <label style={{ fontSize: '10px', fontWeight: '600', color: '#64748b' }}>Select Template</label>
                                <select
                                    value={actionCfg.template_id || ''}
                                    onChange={e => updateBtn(btnName, 'template_id', e.target.value)}
                                    style={{ padding: '6px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '11px', outline: 'none', background: '#fff' }}
                                >
                                    <option value="">-- Select Template --</option>
                                    {templates.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>

                                {triggeredTpl && (
                                    <div style={{ background: '#fff', padding: '6px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '10px', color: '#475569' }}>
                                        <strong>Preview:</strong> "{triggeredTpl.body}"
                                    </div>
                                )}

                                {tplVarCount > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                                        <label style={{ fontSize: '10px', fontWeight: '700', color: 'var(--brand-purple)' }}>
                                            Template Variables ({tplVarCount})
                                        </label>
                                        {Array.from({ length: tplVarCount }).map((_, vIdx) => {
                                            const currentVarVals = (actionCfg.template_variables || {}).body_variables || [];
                                            return (
                                                <div key={vIdx} style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: '#fff', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <span style={{ fontSize: '10px', fontWeight: '700', color: '#475569' }}>Variable &#123;&#123;{vIdx + 1}&#125;&#125;</span>
                                                        <button type="button" onClick={() => updateBtnVar(btnName, vIdx, '{{contact.name}}')} style={{ fontSize: '9px', background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', borderRadius: '4px', padding: '1px 4px', cursor: 'pointer', fontWeight: '600' }}>+ Name</button>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        placeholder={`Value for {{${vIdx + 1}}}`}
                                                        value={currentVarVals[vIdx] || ''}
                                                        onChange={e => updateBtnVar(btnName, vIdx, e.target.value)}
                                                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px', outline: 'none' }}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {triggeredTpl && (nestedButtons.length > 0 || hasNestedConfig) && (
                                    <div style={{ marginTop: '6px', paddingLeft: '8px', borderLeft: '2px solid var(--brand-purple)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--brand-purple)' }}>
                                            ↳ When "{triggeredTpl.name}" is clicked:
                                        </div>
                                        <ButtonActionsEditor
                                            buttons={nestedButtons}
                                            actions={actionCfg.next_button_actions || {}}
                                            onChange={(nextActions) => updateBtn(btnName, 'next_button_actions', nextActions)}
                                            templates={templates}
                                            followUpWorkflows={followUpWorkflows}
                                            depth={depth + 1}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {actionCfg.action === 'TRIGGER_FOLLOWUP' && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', alignItems: 'center', marginTop: '4px' }}>
                                <label style={{ fontSize: '10px', fontWeight: '600', color: '#64748b' }}>Select Workflow</label>
                                <select
                                    value={actionCfg.followup_workflow_id || ''}
                                    onChange={e => updateBtn(btnName, 'followup_workflow_id', e.target.value)}
                                    style={{ padding: '6px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '11px', outline: 'none', background: '#fff' }}
                                >
                                    <option value="">-- Select Workflow --</option>
                                    {followUpWorkflows.map(wf => (
                                        <option key={wf.id} value={wf.id}>{wf.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {actionCfg.action === 'FOLLOWUP_REPLY' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                                <label style={{ fontSize: '10px', fontWeight: '600', color: '#64748b' }}>Reply Text</label>
                                <textarea
                                    rows="2"
                                    placeholder="Enter text reply message to send..."
                                    value={actionCfg.reply_text || ''}
                                    onChange={e => updateBtn(btnName, 'reply_text', e.target.value)}
                                    style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '11px', outline: 'none', resize: 'vertical' }}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const replaceVariables = (text, values) => {
    let result = text;
    Object.keys(values).forEach(key => {
        const val = values[key];
        const placeholder = `{{${key}}}`;
        const replacement = val && val.trim() ? val : placeholder;
        result = result.split(placeholder).join(replacement);
    });
    return result;
};

// WhatsApp text formatting: *bold*, _italic_, ~strikethrough~
const formatWhatsAppText = (text) => {
    if (!text) return '';
    // Split into parts, preserving formatting markers
    const parts = [];
    let remaining = text;
    const regex = /(\*[^\s*\n](?:[^*]*?[^\s*\n])?\*|_[^\s_\n](?:[^_]*?[^\s_\n])?_|~[^\s~\n](?:[^~]*?[^\s~\n])?~)/g;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(remaining)) !== null) {
        if (match.index > lastIndex) {
            parts.push(remaining.substring(lastIndex, match.index));
        }
        const raw = match[0];
        if (raw.startsWith('*') && raw.endsWith('*')) {
            parts.push(<strong key={match.index}>{raw.slice(1, -1)}</strong>);
        } else if (raw.startsWith('_') && raw.endsWith('_')) {
            parts.push(<em key={match.index}>{raw.slice(1, -1)}</em>);
        } else if (raw.startsWith('~') && raw.endsWith('~')) {
            parts.push(<s key={match.index}>{raw.slice(1, -1)}</s>);
        }
        lastIndex = match.index + raw.length;
    }
    if (lastIndex < remaining.length) {
        parts.push(remaining.substring(lastIndex));
    }
    return parts.length > 0 ? parts : text;
};

const SendWhatsAppModal = ({ show, onClose, selectedLeads }) => {
  const [configs, setConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isSending, setIsSending] = useState(false);

  const [variableValues, setVariableValues] = useState({});
  const [buttonActions, setButtonActions] = useState({});
  const [followUpWorkflows, setFollowUpWorkflows] = useState([]);
  const [fullTemplateDetails, setFullTemplateDetails] = useState(null);

  // Get clean host port dynamically or fallback to localhost:8000
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const cleanPort = isLocal ? 'http://localhost:8000' : `${window.location.protocol}//hrmbackendapi.${window.location.hostname.replace('hrm.', '')}`;
  const WA_BASE = `${cleanPort}/api/wa`;

  // Fetch configs, approved templates, and workflows on mount/show
  useEffect(() => {
    if (show) {
      // Get HRM employee ID for header
      const hrmUser = sessionStorage.getItem('user');
      let empIdHeader = {};
      if (hrmUser) {
        try {
          const parsed = JSON.parse(hrmUser);
          if (parsed && parsed.EmployeeId) {
            empIdHeader = { 'X-HRM-Employee-ID': parsed.EmployeeId };
          }
        } catch (e) {
          console.error(e);
        }
      }

      // Fetch configs
      axios.get(`${WA_BASE}/whatsapp/config/`, { headers: empIdHeader })
        .then((res) => {
          const configsList = res.data.results || res.data || [];
          setConfigs(configsList);
          if (configsList.length > 0) {
            setSelectedConfigId(configsList[0].id);
          }
        })
        .catch((err) => console.error('Failed to load configs:', err));

      // Fetch message templates
      axios.get(`${WA_BASE}/templates/?status=APPROVED`, { headers: empIdHeader })
        .then((res) => {
          setTemplates(res.data.results || res.data || []);
        })
        .catch((err) => console.error('Failed to load templates:', err));

      // Fetch follow-up workflows
      axios.get(`${WA_BASE}/follow-ups/`, { headers: empIdHeader })
        .then((res) => {
          setFollowUpWorkflows(res.data?.results || res.data || []);
        })
        .catch((err) => console.error('Failed to load workflows:', err));
    }
  }, [show]);

  // Fetch full template details (buttons, variables) when a template is selected
  useEffect(() => {
    if (selectedTemplate) {
      const hrmUser = sessionStorage.getItem('user');
      let empIdHeader = {};
      if (hrmUser) {
        try {
          const parsed = JSON.parse(hrmUser);
          if (parsed && parsed.EmployeeId) {
            empIdHeader = { 'X-HRM-Employee-ID': parsed.EmployeeId };
          }
        } catch (e) {
          console.error(e);
        }
      }
      axios.get(`${WA_BASE}/templates/${selectedTemplate.id}/`, { headers: empIdHeader })
        .then((res) => {
          const fullT = res.data;
          setFullTemplateDetails(fullT);
          
          // Extract variables
          const bodyText = fullT.body || '';
          const headerText = fullT.header_text || '';
          const variablesList = [];
          const regex = /\{\{(\w+)\}\}/g;
          let match;
          while ((match = regex.exec(headerText)) !== null) {
            variablesList.push(match[1]);
          }
          while ((match = regex.exec(bodyText)) !== null) {
            variablesList.push(match[1]);
          }
          const uniqueVars = Array.from(new Set(variablesList));
          
          // Setup variable values state
          const initVars = {};
          uniqueVars.forEach(v => {
            if (v === 'name' || v === 'customer_name' || v === 'contact_name') {
              initVars[v] = '{{contact.name}}'; // Auto-fill with placeholder for bulk resolved name
            } else {
              initVars[v] = '';
            }
          });
          setVariableValues(initVars);
          setButtonActions({});
        })
        .catch((err) => console.error('Failed to load template details:', err));
    } else {
      setFullTemplateDetails(null);
      setVariableValues({});
      setButtonActions({});
    }
  }, [selectedTemplate?.id]);

  const handleSend = async () => {
    if (!selectedTemplate) {
      toast.warning('Please select a message template.');
      return;
    }
    setIsSending(true);

    const hrmUser = sessionStorage.getItem('user');
    let empIdHeader = {};
    if (hrmUser) {
      try {
        const parsed = JSON.parse(hrmUser);
        if (parsed && parsed.EmployeeId) {
          empIdHeader = { 'X-HRM-Employee-ID': parsed.EmployeeId };
        }
      } catch (e) {
        console.error(e);
      }
    }

    const components = [];
    const headerText = selectedTemplate.header_text || '';
    const bodyText = selectedTemplate.body || '';

    const extractVarKeys = (text) => {
      const regex = /\{\{(\w+)\}\}/g;
      const keys = [];
      let match;
      while ((match = regex.exec(text)) !== null) {
        keys.push(match[1]);
      }
      return [...new Set(keys)];
    };

    const headerVarKeys = extractVarKeys(headerText);
    const bodyVarKeys = extractVarKeys(bodyText);

    if (headerVarKeys.length > 0) {
      components.push({
        type: 'header',
        parameters: headerVarKeys.map((key) => ({
          type: 'text',
          parameter_name: key,
          text: String(variableValues[key] || ''),
        }))
      });
    }

    if (bodyVarKeys.length > 0) {
      components.push({
        type: 'body',
        parameters: bodyVarKeys.map((key) => ({
          type: 'text',
          parameter_name: key,
          text: String(variableValues[key] || ''),
        }))
      });
    }

    try {
      const response = await axios.post(
        `${WA_BASE}/leads/send-whatsapp/`,
        {
          config_id: selectedConfigId,
          template_name: selectedTemplate.name,
          template_language: selectedTemplate.language || 'en_US',
          components,
          button_actions: buttonActions,
          leads: selectedLeads.map((l) => ({
            name: l.candidate_name,
            phone: l.candidate_phone,
            email: l.candidate_email,
          })),
        },
        { headers: empIdHeader }
      );

      const sent = response.data.sent || 0;
      const failed = response.data.failed || 0;
      toast.success(`Sent template to ${sent} leads successfully!${failed ? ` (${failed} failed)` : ''}`);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error('Failed to send WhatsApp messages.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <div>
          <Modal.Title style={{ fontWeight: 700, color: '#333' }}>Send WhatsApp Message</Modal.Title>
          <small style={{ color: '#25D366', fontWeight: 600 }}>
            Sending to {selectedLeads.length} selected leads
          </small>
        </div>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column flex-md-row gap-4" style={{ alignItems: 'flex-start' }}>
        {/* LEFT COLUMN: Input Selection Form */}
        <div className="flex-grow-1" style={{ flex: 1 }}>
          <div className="mb-4">
            <label className="form-label" style={{ fontWeight: 600, color: '#444' }}>
              CHOOSE WHATSAPP NUMBER
            </label>
            <select
              className="form-select form-control"
              value={selectedConfigId}
              onChange={(e) => setSelectedConfigId(e.target.value)}
              style={{ padding: '10px', borderRadius: '8px' }}
            >
              {configs.map((cfg) => (
                <option key={cfg.id} value={cfg.id}>
                  {cfg.name} ({cfg.phone_number_id})
                </option>
              ))}
              {configs.length === 0 && <option value="">No configs found</option>}
            </select>
          </div>

          <div className="mb-4">
            <label className="form-label" style={{ fontWeight: 600, color: '#444' }}>
              CHOOSE MESSAGE TEMPLATE
            </label>
            <select
              className="form-select form-control mb-2"
              onChange={(e) => {
                const t = templates.find((temp) => temp.name === e.target.value);
                setSelectedTemplate(t || null);
              }}
              style={{ padding: '10px', borderRadius: '8px' }}
            >
              <option value="">Select Template</option>
              {templates.map((temp) => (
                <option key={temp.id} value={temp.name}>
                  {temp.name} ({temp.category || 'Utility'})
                </option>
              ))}
            </select>
          </div>

          {Object.keys(variableValues).length > 0 && (
            <div className="mb-4">
              <label className="form-label" style={{ fontWeight: 600, color: '#444' }}>
                PERSONALIZE VARIABLES
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {Object.keys(variableValues).sort((a,b) => {
                  const numA = parseInt(a, 10);
                  const numB = parseInt(b, 10);
                  if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                  return String(a).localeCompare(String(b));
                }).map((v) => (
                  <div key={v} style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--brand-purple)' }}>Variable &#123;&#123;{v}&#125;&#125;</span>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button 
                          type="button" 
                          onClick={() => setVariableValues(prev => ({ ...prev, [v]: (prev[v] || '') + '{{contact.name}}' }))} 
                          style={{ fontSize: '10px', background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontWeight: '600' }}
                        >
                          + Contact Name
                        </button>
                        <button 
                          type="button" 
                          onClick={() => setVariableValues(prev => ({ ...prev, [v]: (prev[v] || '') + '{{contact.phone_number}}' }))} 
                          style={{ fontSize: '10px', background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontWeight: '600' }}
                        >
                          + Contact Phone
                        </button>
                        <button 
                          type="button" 
                          onClick={() => setVariableValues(prev => ({ ...prev, [v]: (prev[v] || '') + '{{contact.email}}' }))} 
                          style={{ fontSize: '10px', background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontWeight: '600' }}
                        >
                          + Contact Email
                        </button>
                      </div>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      style={{ fontSize: '12px', background: '#fff', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                      placeholder={`Value for variable ${v}...`}
                      value={variableValues[v] || ''}
                      onChange={(e) => setVariableValues(prev => ({ ...prev, [v]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {fullTemplateDetails && getTemplateReplyButtons(fullTemplateDetails).length > 0 && (
            <div className="mb-4">
              <label className="form-label" style={{ fontWeight: 600, color: '#444', marginBottom: '8px' }}>
                BUTTON REPLY FOLLOW-UP ACTIONS
              </label>
              <ButtonActionsEditor
                buttons={getTemplateReplyButtons(fullTemplateDetails)}
                actions={buttonActions}
                onChange={setButtonActions}
                templates={templates}
                followUpWorkflows={followUpWorkflows}
              />
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Live Message Preview - sticky so it stays visible while left scrolls */}
        <div
          className="d-flex flex-column"
          style={{
            flex: 1,
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            backgroundColor: '#efeae2',
            backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")',
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid #ddd',
            minHeight: '220px',
            maxHeight: '350px',
            overflowY: 'auto',
          }}
        >
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#666', marginBottom: '8px' }}>
            LIVE MESSAGE PREVIEW
          </div>
          {selectedTemplate ? (
            <div
              className="p-3 shadow-sm"
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                borderTopRightRadius: '0px',
                alignSelf: 'flex-start',
                maxWidth: '90%',
                fontFamily: 'sans-serif',
                fontSize: '14px',
                lineHeight: '1.4',
                color: '#303030',
                whiteSpace: 'pre-wrap',
                position: 'relative',
              }}
            >
              {formatWhatsAppText(replaceVariables(selectedTemplate.body || selectedTemplate.text || '', variableValues))}
              <div
                className="text-end"
                style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}
              >
                12:00 PM
              </div>
            </div>
          ) : (
            <div className="m-auto text-center text-muted" style={{ fontSize: '13px' }}>
              Select a template to view preview
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          onClick={onClose}
          className="btn btn-light"
          style={{ fontWeight: 600, padding: '8px 20px', borderRadius: '8px' }}
        >
          Cancel
        </button>
        <button
          onClick={handleSend}
          disabled={!selectedTemplate || isSending}
          className="btn btn-success"
          style={{
            fontWeight: 600,
            padding: '8px 20px',
            borderRadius: '8px',
            backgroundColor: '#25D366',
            borderColor: '#25D366',
            color: '#fff',
          }}
        >
          {isSending ? 'Sending...' : 'Send Message'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default SendWhatsAppModal;


